import { useNavigate, useLocation } from "react-router-dom";
import { useState, useEffect, useRef, useCallback } from "react";
import { getUser, clearSession } from "../config/auth";
import { canCreateTask, canManageSettings } from "../config/rbac";
import { apiFetch } from "../config/api";
import { SUITE_APPS, getActiveModuleKey } from "../config/suiteApps";

const PAGE_TITLES = {
  "/dashboard":         "Dashboard",
  "/tasks":             "Tasks",
  "/projects":          "Projects",
  "/moms":              "Meetings",
  "/users":             "Users & Roles",
  "/departments":       "Departments",
  "/audit":             "Audit Logs",
  "/validations":       "Validations",
  "/profile":           "My Profile",
  "/settings":          "Settings",
  "/timeline-requests": "Timeline Requests",
  "/crm":               "CRM",
  "/hrms":              "HRMS",
  "/finance-admin":     "Finance/Admin",
  "/client-portal":     "Client Portal",
  "/reports":           "Reports & Analytics",
  "/marketplace":       "Marketplace",
};

const ROLE_COLORS = {
  Admin:              { bg: "rgba(239,68,68,0.09)",   color: "var(--danger-text)", label: "Admin" },
  Director:           { bg: "rgba(109,40,217,0.09)",  color: "#5b21b6", label: "Director" },
  CXO:                { bg: "rgba(124,58,237,0.09)",  color: "#6d28d9", label: "CXO" },
  Manager:            { bg: "rgba(245,158,11,0.09)",  color: "var(--warn-text)", label: "Manager" },
  Head:               { bg: "rgba(14,165,233,0.09)",  color: "#0369a1", label: "Head" },
  Staff:              { bg: "rgba(16,185,129,0.09)",  color: "#047857", label: "Staff" },
};

function timeAgo(dateStr) {
  const diff = Math.floor((Date.now() - new Date(dateStr)) / 1000);
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

// ── Notification Bell ─────────────────────────────────────────────────────────
function NotificationBell({ navigate }) {
  const [notifications, setNotifications] = useState([]);
  const [open, setOpen] = useState(false);
  const dropdownRef = useRef(null);

  const fetchNotifications = useCallback(async () => {
    try {
      const res = await apiFetch("/api/notifications");
      if (res.ok) setNotifications(await res.json());
    } catch (_) {}
  }, []);

  useEffect(() => {
    fetchNotifications();
    const onFocus = () => fetchNotifications();
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [fetchNotifications]);

  useEffect(() => {
    function handleClick(e) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  const unread = notifications.filter(n => !n.isRead).length;

  async function handleNotificationClick(n) {
    if (!n.isRead) {
      await apiFetch(`/api/notifications/${n.id}/read`, { method: "PUT" });
      setNotifications(prev => prev.map(x => x.id === n.id ? { ...x, isRead: true } : x));
    }
    if (n.taskId) navigate(`/tasks/${n.taskId}`);
    setOpen(false);
  }

  async function markAllRead() {
    await apiFetch("/api/notifications/read-all", { method: "PUT" });
    setNotifications(prev => prev.map(n => ({ ...n, isRead: true })));
  }

  return (
    <div ref={dropdownRef} style={{ position: "relative" }}>
      <button
        onClick={() => setOpen(o => !o)}
        aria-label="Notifications"
        style={{
          position: "relative",
          background: open ? "var(--surface-2)" : "transparent",
          border: "1px solid " + (open ? "var(--border)" : "transparent"),
          cursor: "pointer",
          padding: "6px 10px",
          borderRadius: 10,
          color: open ? "var(--text)" : "var(--text-2)",
          display: "flex", alignItems: "center", gap: 6,
          transition: "background 0.15s, border-color 0.15s, color 0.15s",
        }}
        onMouseEnter={e => {
          if (!open) {
            e.currentTarget.style.background = "var(--surface-2)";
            e.currentTarget.style.borderColor = "var(--border)";
          }
        }}
        onMouseLeave={e => {
          if (!open) {
            e.currentTarget.style.background = "transparent";
            e.currentTarget.style.borderColor = "transparent";
          }
        }}
      >
        <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
          <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/>
          <path d="M13.73 21a2 2 0 0 1-3.46 0"/>
        </svg>
        {unread > 0 ? (
          <span style={{
            background: "var(--danger)", color: "#fff",
            borderRadius: 999, fontSize: 10, fontWeight: 700,
            minWidth: 18, height: 18, display: "flex", alignItems: "center", justifyContent: "center",
            padding: "0 4px", lineHeight: 1,
          }}>
            {unread > 9 ? "9+" : unread}
          </span>
        ) : (
          <span style={{ fontSize: 12, fontWeight: 500, color: "var(--text-3)" }}>Notifications</span>
        )}
      </button>

      {open && (
        <div style={{
          position: "absolute", right: 0, top: "calc(100% + 10px)", zIndex: 999,
          width: 360, maxHeight: 440, overflowY: "auto",
          background: "var(--surface)", border: "1px solid var(--border)",
          borderRadius: 14, boxShadow: "0 12px 32px rgba(0,0,0,0.13)",
        }}>
          <div style={{
            display: "flex", justifyContent: "space-between", alignItems: "center",
            padding: "14px 16px 10px", borderBottom: "1px solid var(--border)",
            position: "sticky", top: 0, background: "var(--surface)", zIndex: 1,
          }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <span style={{ fontWeight: 700, fontSize: 13, color: "var(--text)" }}>Notifications</span>
              {unread > 0 && (
                <span style={{
                  background: "var(--danger)", color: "#fff", borderRadius: 999,
                  fontSize: 10, fontWeight: 700, padding: "1px 6px",
                }}>
                  {unread}
                </span>
              )}
            </div>
            {unread > 0 && (
              <button onClick={markAllRead} style={{
                fontSize: 12, color: "#2563eb", background: "none",
                border: "none", cursor: "pointer", fontWeight: 600,
              }}>
                Mark all read
              </button>
            )}
          </div>

          {notifications.length === 0 ? (
            <div style={{ padding: "32px 16px", textAlign: "center", color: "var(--text-3)", fontSize: 13 }}>
              <div style={{ fontSize: 28, marginBottom: 8 }}>🔔</div>
              You're all caught up
            </div>
          ) : (
            notifications.map(n => (
              <div
                key={n.id}
                onClick={() => handleNotificationClick(n)}
                style={{
                  padding: "11px 16px",
                  cursor: n.taskId ? "pointer" : "default",
                  background: n.isRead ? "transparent" : "rgba(37,99,235,0.04)",
                  borderBottom: "1px solid var(--border)",
                  display: "flex", gap: 10, alignItems: "flex-start",
                  transition: "background 0.12s",
                }}
                onMouseEnter={e => { if (n.taskId) e.currentTarget.style.background = "var(--surface-2)"; }}
                onMouseLeave={e => { e.currentTarget.style.background = n.isRead ? "transparent" : "rgba(37,99,235,0.04)"; }}
              >
                <div style={{
                  width: 7, height: 7, borderRadius: "50%", flexShrink: 0, marginTop: 5,
                  background: n.isRead ? "transparent" : "#2563eb",
                }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 12, color: "var(--text)", lineHeight: 1.5 }}>{n.message}</div>
                  <div style={{ fontSize: 11, color: "var(--text-3)", marginTop: 3 }}>{timeAgo(n.createdAt)}</div>
                </div>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}

// ── User Avatar Dropdown ──────────────────────────────────────────────────────
function UserMenu({ user, navigate }) {
  const [open, setOpen] = useState(false);
  const menuRef = useRef(null);
  const role = user?.role || "Staff";
  const rc = ROLE_COLORS[role] || ROLE_COLORS.Staff;
  const showSettings = canManageSettings(role);
  const initial = (user?.name || user?.email || "U")[0].toUpperCase();

  useEffect(() => {
    function handleClick(e) {
      if (menuRef.current && !menuRef.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  function logout() {
    clearSession();
    navigate("/login");
  }

  return (
    <div ref={menuRef} style={{ position: "relative" }}>
      <button
        onClick={() => setOpen(o => !o)}
        aria-label="User menu"
        style={{
          display: "flex", alignItems: "center", gap: 8,
          background: open ? "var(--surface-2)" : "none",
          border: "1px solid " + (open ? "var(--border)" : "transparent"),
          borderRadius: 10, padding: "4px 10px 4px 4px",
          cursor: "pointer", transition: "background 0.15s, border-color 0.15s",
        }}
      >
        {/* Avatar circle */}
        <div style={{
          width: 30, height: 30, borderRadius: "50%",
          background: rc.color, color: "#fff",
          display: "flex", alignItems: "center", justifyContent: "center",
          fontSize: 12, fontWeight: 700, flexShrink: 0,
        }}>
          {initial}
        </div>
        {/* Name + role */}
        <div style={{ textAlign: "left", lineHeight: 1.3 }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: "var(--text)", whiteSpace: "nowrap", maxWidth: 130, overflow: "hidden", textOverflow: "ellipsis" }}>
            {user?.name || user?.email || "User"}
          </div>
          <div style={{
            fontSize: 10, fontWeight: 700, letterSpacing: "0.04em",
            textTransform: "uppercase", color: rc.color,
          }}>
            {rc.label}
          </div>
        </div>
        {/* Chevron */}
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="var(--text-3)" strokeWidth="2.5" strokeLinecap="round">
          <polyline points={open ? "18 15 12 9 6 15" : "6 9 12 15 18 9"} />
        </svg>
      </button>

      {open && (
        <div style={{
          position: "absolute", right: 0, top: "calc(100% + 10px)", zIndex: 999,
          width: 250,
          background: "var(--surface)", border: "1px solid var(--border)",
          borderRadius: 14, boxShadow: "0 12px 32px rgba(0,0,0,0.13)",
          overflow: "hidden",
        }}>
          {/* Header */}
          <div style={{
            padding: "14px 16px 12px",
            borderBottom: "1px solid var(--border)",
          }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <div style={{
                width: 36, height: 36, borderRadius: "50%",
                background: rc.color, color: "#fff",
                display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: 14, fontWeight: 700, flexShrink: 0,
              }}>
                {initial}
              </div>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: "var(--text)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {user?.name || user?.email}
                </div>
                {user?.name && user?.email && (
                  <div style={{ fontSize: 11, color: "var(--text-3)", marginTop: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {user.email}
                  </div>
                )}
                <span style={{
                  display: "inline-block", marginTop: 4,
                  padding: "1px 7px", borderRadius: 999,
                  fontSize: 10, fontWeight: 700, letterSpacing: "0.04em",
                  textTransform: "uppercase",
                  background: rc.bg, color: rc.color,
                }}>
                  {rc.label}
                </span>
              </div>
            </div>
          </div>

          {/* Menu items */}
          <div style={{ padding: "6px 0" }}>
            <MenuItem
              icon={
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round">
                  <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/>
                </svg>
              }
              label="My Profile"
              onClick={() => { setOpen(false); navigate("/profile"); }}
            />
            {showSettings && (
              <MenuItem
                icon={
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round">
                    <circle cx="12" cy="12" r="3"/>
                    <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/>
                  </svg>
                }
                label="Settings"
                onClick={() => { setOpen(false); navigate("/settings"); }}
              />
            )}
          </div>

          {/* Divider + Sign out */}
          <div style={{ borderTop: "1px solid var(--border)", padding: "6px 0" }}>
            <MenuItem
              icon={
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round">
                  <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/>
                </svg>
              }
              label="Sign out"
              onClick={logout}
              danger
            />
          </div>
        </div>
      )}
    </div>
  );
}

function MenuItem({ icon, label, onClick, danger }) {
  const [hovered, setHovered] = useState(false);
  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        width: "100%", display: "flex", alignItems: "center", gap: 10,
        padding: "8px 16px", background: hovered ? "var(--surface-2)" : "none",
        border: "none", cursor: "pointer", textAlign: "left",
        color: danger ? (hovered ? "#dc2626" : "#ef4444") : "var(--text)",
        fontSize: 13, fontWeight: 500, transition: "background 0.12s, color 0.12s",
      }}
    >
      <span style={{ color: danger ? "inherit" : "var(--text-2)", flexShrink: 0 }}>{icon}</span>
      {label}
    </button>
  );
}

// ── Navbar ────────────────────────────────────────────────────────────────────
function GlobalSearch({ navigate }) {
  const [query, setQuery] = useState("");

  function submit(e) {
    e.preventDefault();
    const value = query.trim();
    navigate(value ? `/tasks?search=${encodeURIComponent(value)}` : "/tasks");
  }

  return (
    <div className="navbar-search">
      <form className="navbar-search-form" onSubmit={submit}>
        <span className="navbar-search-icon" aria-hidden="true">
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round">
            <circle cx="11" cy="11" r="7" />
            <line x1="16.5" y1="16.5" x2="21" y2="21" />
          </svg>
        </span>
        <input
          className="navbar-search-input"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search tasks, projects, people"
          aria-label="Global search"
        />
        <button className="navbar-search-submit" type="submit" aria-label="Search">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
            <path d="M5 12h14" />
            <path d="m12 5 7 7-7 7" />
          </svg>
        </button>
      </form>
    </div>
  );
}

function AppLauncher({ navigate, currentPath }) {
  const [open, setOpen] = useState(false);
  const [enabledModules, setEnabledModules] = useState(null);
  const launcherRef = useRef(null);
  const activeModuleKey = getActiveModuleKey(currentPath);
  const apps = SUITE_APPS.filter((app) => !app.hidden).map((app) => ({
    ...app,
    disabled: !app.implemented || (enabledModules ? !enabledModules.has(app.moduleKey) : false),
    active: activeModuleKey === app.moduleKey,
  }));

  useEffect(() => {
    function handleClick(e) {
      if (launcherRef.current && !launcherRef.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  useEffect(() => {
    apiFetch("/api/modules")
      .then((r) => (r.ok ? r.json() : []))
      .then((data) => {
        if (!Array.isArray(data)) return;
        setEnabledModules(new Set(data.filter((m) => m.isEnabled).map((m) => m.moduleKey)));
      })
      .catch(() => {});
  }, []);

  function openApp(path) {
    setOpen(false);
    navigate(path);
  }

  return (
    <div ref={launcherRef} style={{ position: "relative" }}>
      <button
        type="button"
        aria-label="Apps"
        title="Apps"
        onClick={() => setOpen(o => !o)}
        style={{
          minWidth: 72,
          height: 36,
          padding: "0 12px",
          borderRadius: 10,
          border: "1px solid var(--border)",
          background: open ? "var(--surface-2)" : "transparent",
          color: open ? "var(--text)" : "var(--text-2)",
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 7,
          cursor: "pointer",
          transition: "background 0.15s, border-color 0.15s, color 0.15s",
          fontSize: 13,
          fontWeight: 700,
        }}
      >
        <svg width="17" height="17" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
          <circle cx="5" cy="5" r="2" />
          <circle cx="12" cy="5" r="2" />
          <circle cx="19" cy="5" r="2" />
          <circle cx="5" cy="12" r="2" />
          <circle cx="12" cy="12" r="2" />
          <circle cx="19" cy="12" r="2" />
          <circle cx="5" cy="19" r="2" />
          <circle cx="12" cy="19" r="2" />
          <circle cx="19" cy="19" r="2" />
        </svg>
        <span>Apps</span>
      </button>

      {open && (
        <div style={{
          position: "absolute",
          right: 0,
          top: "calc(100% + 10px)",
          zIndex: 999,
          width: 360,
          maxWidth: "calc(100vw - 24px)",
          background: "var(--surface)",
          border: "1px solid var(--border)",
          borderRadius: 14,
          boxShadow: "0 12px 32px rgba(0,0,0,0.13)",
          padding: 12,
        }}>
          <div style={{
            display: "grid",
            gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
            gap: 8,
          }}>
            {apps.map(app => (
              <button
                key={app.label}
                type="button"
                disabled={app.disabled}
                aria-disabled={app.disabled ? "true" : undefined}
                aria-current={app.active ? "page" : undefined}
                title={app.disabled ? `${app.label} coming soon` : app.active ? `${app.label} active` : app.label}
                onClick={app.disabled ? undefined : () => openApp(app.path)}
                style={{
                  position: "relative",
                  minHeight: 80,
                  border: app.active ? `2px solid ${app.color}` : "1px solid var(--border)",
                  borderRadius: 10,
                  background: app.disabled ? "var(--surface-2)" : app.active ? app.softColor : "var(--surface)",
                  color: app.disabled ? "var(--text-3)" : "var(--text)",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 7,
                  cursor: app.disabled ? "not-allowed" : "pointer",
                  opacity: app.disabled ? 0.55 : 1,
                  fontSize: 12,
                  fontWeight: 700,
                  lineHeight: 1.2,
                  boxShadow: app.active ? `0 0 0 3px ${app.softColor}` : "none",
                }}
              >
                {app.active && !app.disabled && (
                  <span
                    aria-hidden="true"
                    style={{
                      position: "absolute",
                      top: 7,
                      right: 7,
                      width: 18,
                      height: 18,
                      borderRadius: "50%",
                      background: app.color,
                      color: "#fff",
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                  </span>
                )}
                <span style={{
                  width: 32,
                  height: 32,
                  borderRadius: 9,
                  background: app.disabled ? "rgba(148,163,184,0.16)" : app.color,
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}>
                  <svg viewBox="0 0 24 24" fill="none" stroke={app.disabled ? "#94a3b8" : "#fff"} strokeWidth={1.75} strokeLinecap="round" strokeLinejoin="round" width={16} height={16} aria-hidden="true">
                    <path d={app.icon} />
                  </svg>
                </span>
                <span style={{ textAlign: "center", whiteSpace: "normal" }}>{app.label}</span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function Navbar({ onCreateTask, onMenuToggle }) {
  const navigate = useNavigate();
  const location = useLocation();
  const user = getUser();
  const role = user?.role || "Staff";
  const allowCreate = canCreateTask(role);
  const orgName = "Tackl";

  const pathKey = "/" + location.pathname.split("/")[1];
  const pageTitle = PAGE_TITLES[pathKey];

  return (
    <header className="navbar">
      {/* Hamburger — mobile only */}
      <button className="hamburger-btn" onClick={onMenuToggle} aria-label="Open menu">
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
          <line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="18" x2="21" y2="18"/>
        </svg>
      </button>

      {/* Left: breadcrumb */}
      <div style={{ display: "flex", alignItems: "center", gap: 6, minWidth: 150 }}>
        <span style={{ fontSize: 12, color: "var(--text-3)", fontWeight: 500 }}>{orgName}</span>
        {pageTitle && (
          <>
            <span style={{ color: "var(--border-strong)", fontSize: 13 }}>/</span>
            <span style={{ fontWeight: 600, fontSize: 13, color: "var(--text)", letterSpacing: 0 }}>
              {pageTitle}
            </span>
          </>
        )}
      </div>

      <GlobalSearch navigate={navigate} />

      {/* Right */}
      <div className="navbar-right">
        {allowCreate && onCreateTask && (
          <button
            className="btn navbar-create-btn"
            onClick={onCreateTask}
          >
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <line x1="12" y1="5" x2="12" y2="19" />
              <line x1="5" y1="12" x2="19" y2="12" />
            </svg>
            New Task
          </button>
        )}

        <NotificationBell navigate={navigate} />

        <AppLauncher navigate={navigate} currentPath={location.pathname} />

        <UserMenu user={user} navigate={navigate} />
      </div>
    </header>
  );
}

export default Navbar;
